import { FieldValues, UseFormReturn } from 'react-hook-form'

import { BackendLoggingData, OrgSmartAccount, OrgTransaction } from '~/types'

type BaseTransactionParams<TFormData> = {
  sender: OrgSmartAccount
  data: TFormData
  loggingData?: BackendLoggingData
}

type TransactionHandlerParams<TFormData extends FieldValues> = {
  methods: UseFormReturn<TFormData>
  senderAccount: OrgSmartAccount
  getLoggingData?: (data: TFormData) => BackendLoggingData | undefined
  createTransactionOnSubmit: (
    shouldExecute: boolean,
    onTransaction?: (transaction: OrgTransaction) => void,
  ) => (params: BaseTransactionParams<TFormData>) => Promise<void>
  approveAndSubmitTransaction?: (params: {
    smartAccount: OrgSmartAccount
    transaction: OrgTransaction
    onConfirmation?: () => void
  }) => void
  onCreation?: (transaction: OrgTransaction) => void
  onConfirmation?: () => void
}

export const createTransactionHandlers = <TFormData extends FieldValues>({
  methods,
  senderAccount,
  getLoggingData,
  createTransactionOnSubmit,
  approveAndSubmitTransaction,
  onCreation,
  onConfirmation,
}: TransactionHandlerParams<TFormData>) => {
  const onExecute = methods.handleSubmit((data: TFormData) =>
    createTransactionOnSubmit(true, (transaction) => {
      if (!senderAccount) throw new Error('Sender smart account not found')
      approveAndSubmitTransaction?.({
        smartAccount: senderAccount,
        transaction,
        onConfirmation,
      })
      onCreation?.(transaction)
    })({
      sender: senderAccount,
      data,
      loggingData: getLoggingData?.(data),
    }),
  )

  const onCreate = methods.handleSubmit((data: TFormData) =>
    createTransactionOnSubmit(false, (transaction) => {
      onCreation?.(transaction)
    })({
      sender: senderAccount,
      data,
      loggingData: getLoggingData?.(data),
    }),
  )

  return { onExecute, onCreate }
}
